import { useRef, useEffect } from 'react';
import type { ErrorInfo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import { Remote, type Injectables } from '@proposify/mfe-tools';
import { trackError, trackDatadogAction } from '@proposify/frontend-observability';
import type { UserEntity } from 'services/entities/UserEntity';
import EmptyState from 'components/emptyState';
import { NotificationsProvider } from 'components/notifications/NotificationsProvider';
import { I18nProvider } from 'providers/I18nProvider';
import PermissionsProvider from 'providers/PermissionsProvider';
import { QueryProvider } from 'providers/QueryProvider';
import { QueryProviderV5 } from 'providers/QueryProviderV5';
import { InjectableUserProvider } from 'providers/UserProvider';
import { ApiUserPermissionsRepository } from 'services/permissions/implementations/ApiUserPermissionsRepository';
import { EditorContentWrapper } from 'pages/editor/editorContentWrapper';
import { ContentType } from 'utils/getContentType';
import { designToken, DesignTokenProvider } from '@proposify/ui';

export interface EditorInjectables extends Injectables {
  user: UserEntity;
  isFrontendUserTokenEnabled: boolean;
  contentId: string;
}

const REQUIRED_ENV_VARS = [
  'VITE_DOCUMENT_SERVICE_API_ENDPOINT',
  'VITE_DOCUMENT_SERVICE_API_HOST',
  'VITE_DOCUMENT_SERVICE_SOCKET_ENDPOINT',
] as const;

function assertRequiredEnvVars(): void {
  const missing = REQUIRED_ENV_VARS.filter((key) => !globalThis.env?.[key]);
  if (missing.length > 0) {
    trackError(new Error(`EditorMicrofrontend: missing required env vars: ${missing.join(', ')}`));
  }
}

function validateInjectables(injectables: object): asserts injectables is EditorInjectables {
  if ('user' in injectables && injectables.user && 'contentId' in injectables) {
    if (typeof injectables.contentId === 'string' && injectables.contentId !== '') {
      return;
    }
  }
  const error = new Error('validation failure, could not load EditorMicrofrontend');
  trackError(error, {
    injectablesFromHost: injectables,
  });
  throw error;
}

const userPermissionsRepository = new ApiUserPermissionsRepository();

// Module-evaluation timestamp — kept only as a secondary/diagnostic measurement (see below).
const moduleEvaluatedAt = performance.now();

// Module scope on purpose: this must survive remounts within the same page session so a
// later remount doesn't re-fire `editor_remote_loaded` against a stale baseline, and so a
// render error after the initial load is correctly reported as a runtime error, not a load
// failure. See __resetEditorRemoteTrackingForTests below for why tests need a reset hook.
let hasTrackedInitialLoad = false;

/** @internal test-only reset hook — do not use outside tests. */
export const __resetEditorRemoteTrackingForTests = () => {
  hasTrackedInitialLoad = false;
};

// A boundary's onError/componentDidCatch only fires if its own recovery render commits without
// itself throwing — re-throwing from the fallback to "pass through" to an ancestor boundary means
// this boundary's onError never fires at all, silently dropping the tracking. So this renders a
// real, actionable fallback instead of null (fixing the blank-page regression) and relies on
// onError firing reliably, rather than trying to bubble the error to the host.
const EditorRemoteFallback = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      headerText={t('editor.remote.load_error_title')}
      supportingText={t('editor.remote.load_error_description')}
      onRetry={() => window.location.reload()}
    />
  );
};

function handleEditorRemoteError(error: unknown, info: ErrorInfo): void {
  const normalizedError = error instanceof Error ? error : new Error(String(error));
  trackError(normalizedError, { errorFrom: 'editor-remote', feature: 'editor', reactErrorInfo: info });
  trackDatadogAction({
    action: hasTrackedInitialLoad ? 'editor_remote_runtime_error' : 'editor_remote_load_failed',
    data: {
      origin: 'editor-remote',
      reason: 'render_error',
      message: normalizedError.message,
      // Same primary signal as the success path — how far into the page session this happened.
      // elapsedMs (module-eval-to-mount) is deliberately omitted here: for a runtime error hours
      // into a session it'd report a meaningless multi-hour "elapsed" value.
      timeSinceNavigationStartMs: Math.round(performance.now()),
    },
  });
}

const EditorRemoteContent = ({ injectables }: { injectables: EditorInjectables }) => {
  useEffect(() => {
    if (hasTrackedInitialLoad) return;
    hasTrackedInitialLoad = true;
    trackDatadogAction({
      action: 'editor_remote_loaded',
      data: {
        origin: 'editor-remote',
        // Primary load-time signal: anchored to performance.timeOrigin, which Module Federation
        // shares with the host page, so this captures true fetch+parse+render latency.
        timeSinceNavigationStartMs: Math.round(performance.now()),
        // Secondary/diagnostic only — measures module-eval-to-mount, not fetch/parse of the chunk.
        elapsedMs: Math.round(performance.now() - moduleEvaluatedAt),
      },
    });
  }, []);

  return (
    // HelmetProvider is required by react-helmet-async and is added independently in each
    // MFE entrypoint, since this app has no single shared root the provider tree can live in.
    <HelmetProvider>
      <I18nProvider>
        <QueryProvider>
          <QueryProviderV5>
            <DesignTokenProvider theme={designToken}>
              <InjectableUserProvider user={injectables.user}>
                <PermissionsProvider repository={userPermissionsRepository}>
                  <NotificationsProvider>
                    <EditorContentWrapper contentId={injectables.contentId} contentType={ContentType.DOCUMENT} />
                  </NotificationsProvider>
                </PermissionsProvider>
              </InjectableUserProvider>
            </DesignTokenProvider>
          </QueryProviderV5>
        </QueryProvider>
      </I18nProvider>
    </HelmetProvider>
  );
};

const EditorMicrofrontend = () => {
  const envAssertedRef = useRef(false);
  if (!envAssertedRef.current) {
    envAssertedRef.current = true;
    assertRequiredEnvVars();
  }

  return (
    <Remote name="editor-remote" validateInjectables={validateInjectables}>
      {(injectables) => (
        <ErrorBoundary fallbackRender={() => <EditorRemoteFallback />} onError={handleEditorRemoteError}>
          <EditorRemoteContent injectables={injectables} />
        </ErrorBoundary>
      )}
    </Remote>
  );
};

const EditorMicrofrontendEntry = () => <EditorMicrofrontend />;
export default EditorMicrofrontendEntry;
